"use client"

import BannerCard from "@/(admin)/modules/ui-builder/config/components/BannerCard"
import { HeroBannerWithBreadcrumbContent } from "@/(admin)/modules/ui-builder/config/themes/tamda-one/HeroBannerWithBreadcrumb/components/HeroBannerWithBreadcrumbContent"
import PromoVideosSection from "@/components/post-detail/PromoVideosSection"
import { t } from "@/languages/i18n"
import useIsMobile from "@/lib/config/lib/use-is-mobile"
import { PostData } from "@/lib/config/types"
import { getLocaleHomeHref, getLocalizedHref } from "@/lib/locale-utils"
import { incrementPostViews } from "@/lib/post-navigation"
import { SiteId } from "@/sites/config"
import { usePostIdStore } from "@/stores/use-post-id-store"
import { BannerData } from "@/types/types"
import { stripStrayBr } from "@/utils/strip-stray-br"
import { TocItem } from "@/utils/extract-table-of-contents"
import { useEffect, useMemo, useRef, useState } from "react"
import NormalContent from "../normal-content"
import { Campaign } from "./Campaign"
import PostHeader from "./PostHeader"
import RelatedPostsSection from "./RelatedPostsSection"
import SidebarPostsSection from "./SidebarPostsSection"
import SocialShareButtons from "./SocialShareButtons"

type PostDetailContentProps = {
  postTitle: string
  categories: Array<{
    id: number
    attributes: {
      name: string
      slug?: string
    }
  }>
  author: string
  publishedAt: string | undefined
  htmlWithIds: string
  tableOfContents: TocItem[]
  relatedPosts: PostData[]
  sidebarPosts: Array<{
    id: string
    title: string
    slug: string
    summary: string
    imageUrl?: string
    publishedAt: string
    categoryId: string
    locale?: string
  }>
  banners: BannerData[]
  settings: {
    facebook?: string
    twitter?: string
  }
  postId?: number
  postSlug?: string
  siteId?: SiteId
  postImage?: string
  description?: string
  isLiveHappening?: boolean
  liveNewsItems?: Array<{
    id?: number
    title: string
    date: string
    detail: string
  }>
  promoVideoUrls?: string[]
  postData: PostData
  locale?: string
}

export default function PostDetailContent({
  postTitle,
  categories,
  author,
  publishedAt,
  htmlWithIds,
  tableOfContents,
  relatedPosts,
  sidebarPosts,
  banners,
  settings,
  postId,
  postSlug,
  siteId,
  postImage = "",
  description = "",
  isLiveHappening = false,
  liveNewsItems = [],
  promoVideoUrls = [],
  postData,
  locale = "vi",
}: PostDetailContentProps) {
  const isMobile = useIsMobile()
  const { setPostData } = usePostIdStore()
  const [liveTab, setLiveTab] = useState<"detail" | "summary">("detail")
  const [liveSortOrder, setLiveSortOrder] = useState<"desc" | "asc">("desc")
  useEffect(() => {
    if (postId && postSlug) {
      setPostData(postSlug, postId)
    }
  }, [postId, postSlug, setPostData])

  // Đếm lượt xem khi mở trang chi tiết (trước đây đếm ở onClick của PostLink,
  // nên bỏ lỡ traffic vào thẳng URL/SEO và đếm cả những cú click không tới nơi).
  // Ref guard: StrictMode ở dev chạy effect 2 lần.
  const countedPostIdRef = useRef<number | null>(null)
  useEffect(() => {
    if (!postId || countedPostIdRef.current === postId) return
    countedPostIdRef.current = postId
    incrementPostViews(postId)
  }, [postId])

  const isTamdaOne = siteId === "tamdagroup.eu"
  const shouldRenderLiveLayout = isLiveHappening

  const breadcrumb = useMemo(() => {
    if (!isTamdaOne || categories.length === 0) return []
    return [
      { title: t("Trang chủ").toUpperCase(), link: getLocaleHomeHref(locale) },
      {
        title: categories[0].attributes?.name?.toUpperCase() || "",
        link: getLocalizedHref(locale, `/category/${categories[0].attributes?.slug || ""}`),
      },
    ]
  }, [categories, isTamdaOne, locale])

  const sortedLiveItems = useMemo(() => {
    const toTs = (value?: string) => {
      if (!value) return 0
      const ts = new Date(value).getTime()
      return Number.isNaN(ts) ? 0 : ts
    }

    return [...liveNewsItems].sort((a, b) => {
      const diff = toTs(a.date) - toTs(b.date)
      return liveSortOrder === "asc" ? diff : -diff
    })
  }, [liveNewsItems, liveSortOrder])

  const formatLiveDate = (value?: string) => {
    if (!value) return ""
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    const pad = (n: number) => n.toString().padStart(2, "0")
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  return (
    <div>
      {isTamdaOne && postImage && (
        <HeroBannerWithBreadcrumbContent
          id="post-detail-hero-banner"
          breadcrumb={breadcrumb}
          image={postImage}
          title={postTitle}
          isTamdaOne={true}
        />
      )}
      {/* Mobile padding used to be `p-[17.5px]` (~11.7vw on iPhone SE).
       * `p-4` (16px) keeps the body text a comfortable thumb-width from the
       * edge without feeling cramped on 320px screens. */}
      <div
        className={`container mx-auto ${
          isTamdaOne ? "max-w-[1290px]" : "max-w-[1160px]"
        } p-4 pb-16 sm:px-4 sm:pt-[58.5px] sm:pb-[84px]`}
      >
        <div className="flex sm:flex-row flex-col gap-[35px]">
          <div className="sm:w-[67.7%] w-full">
            {/* The post itself is a single <article> so assistive tech /
             * search can identify the main content unit (PostHeader + body
             * + share). Banners, promos and related posts sit outside —
             * they're peripheral, not part of the article. */}
            <article className="flex flex-col gap-0 sm:gap-2">
              <PostHeader
                postTitle={postTitle}
                categories={categories}
                author={author}
                publishedAt={publishedAt}
                siteId={siteId}
                description={description}
                isLiveHappening={shouldRenderLiveLayout}
                locale={locale}
              />

              {shouldRenderLiveLayout ? (
                <div className="py-4">
                  <div className="flex items-center border-b border-public-border-dashed">
                    <button
                      type="button"
                      className={`live-tab-button ${liveTab === "detail" ? "live-tab-active" : ""}`}
                      onClick={() => setLiveTab("detail")}
                    >
                      {t("Chi tiết")}
                    </button>
                    <button
                      type="button"
                      className={`live-tab-button ${liveTab === "summary" ? "live-tab-active" : ""}`}
                      onClick={() => setLiveTab("summary")}
                    >
                      {t("Tổng thuật")}
                    </button>
                  </div>

                  {liveTab === "detail" ? (
                    <div className="pt-5">
                      <div className="mb-8 flex items-center gap-2">
                        <button
                          type="button"
                          className={`live-sort-button ${liveSortOrder === "desc" ? "live-sort-active" : ""}`}
                          onClick={() => setLiveSortOrder("desc")}
                        >
                          {t("Mới nhất")}
                        </button>
                        <button
                          type="button"
                          className={`live-sort-button ${liveSortOrder === "asc" ? "live-sort-active" : ""}`}
                          onClick={() => setLiveSortOrder("asc")}
                        >
                          {t("Cũ nhất")}
                        </button>
                      </div>

                      {sortedLiveItems.length === 0 ? (
                        <div className="py-6 text-sm text-public-fg-muted">{t("Hiện chưa có diễn biến live.")}</div>
                      ) : (
                        <div className="flex flex-col">
                          {sortedLiveItems.map((item, index) => (
                            <article
                              key={`${item.id ?? "live-item"}-${index}-${item.date}`}
                              className="border-b border-public-border-muted py-5 first:pt-0"
                            >
                              <div className="mb-2 text-[12px] leading-[20px] text-public-fg-muted">
                                {formatLiveDate(item.date)}
                              </div>
                              <h3 className="mb-2 text-[24px] leading-[45px] font-bold text-public-fg-strong">
                                {item.title}
                              </h3>
                              <div
                                className="prose prose-lg max-w-none live-item-detail text-[16px]"
                                dangerouslySetInnerHTML={{ __html: stripStrayBr(item.detail || "") }}
                              />
                            </article>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="prose prose-lg max-w-none pt-4">
                      <NormalContent content={htmlWithIds} tableOfContents={tableOfContents} />
                      {isTamdaOne && postData?.attributes?.campaign && <Campaign postData={postData} />}
                    </div>
                  )}
                  <div className="border-t border-public-border-dashed mt-4"></div>
                </div>
              ) : (
                <div className="prose prose-lg max-w-none py-4 border-b border-public-border-dashed">
                  <NormalContent content={htmlWithIds} tableOfContents={tableOfContents} />
                  {isTamdaOne && postData?.attributes?.campaign && <Campaign postData={postData} />}
                </div>
              )}
              <SocialShareButtons facebook={settings.facebook} twitter={settings.twitter} />
            </article>
            {banners.length > 0 && !isTamdaOne && (
              <div className="block sm:hidden flex-col gap-4">
                {banners.map(b => (
                  <BannerCard key={b.id} banner={b} isMobile={true} />
                ))}
              </div>
            )}
            <div className="block sm:hidden flex-col gap-4">
              <PromoVideosSection promoVideoUrls={promoVideoUrls} />
            </div>

            {!isTamdaOne && <RelatedPostsSection posts={relatedPosts} isTamdaOne={false} />}
          </div>

          <aside className="sm:w-[32.3%] w-full">
            <div className="flex flex-col gap-5">
              <h2 className="text-[13px] leading-[20.15px] text-public-fg font-extrabold uppercase">
                {t("Tin nổi bật")}
              </h2>
              <SidebarPostsSection posts={sidebarPosts} locale={locale} />

              {banners.length > 0 && !isTamdaOne && (
                <div className="hidden sm:block flex-col gap-4">
                  {banners.map(b => (
                    <BannerCard key={b.id} banner={b} isMobile={isMobile} />
                  ))}
                </div>
              )}

              {/* Promo Videos Section - displayed in sidebar on desktop */}
              <div className="hidden sm:block">
                <PromoVideosSection promoVideoUrls={promoVideoUrls} />
              </div>
            </div>
          </aside>
        </div>
        {isTamdaOne && <RelatedPostsSection posts={relatedPosts} isTamdaOne={true} />}
      </div>
      <style jsx>{`
        .live-tab-button {
          border: 1px solid transparent;
          border-bottom: 0;
          background: transparent;
          padding: 8px 24px;
          color: #212529;
          font-size: 18px;
          font-weight: 400;
          line-height: 38px;
          cursor: pointer;
          margin-bottom: -1px;
          transition: all 0.2s ease;
        }
        .live-tab-active {
          border-color: #ced4da;
          background: #fff;
          font-weight: 600;
          border-top-left-radius: 4px;
          border-top-right-radius: 4px;
        }
        .live-sort-button {
          border: 0;
          border-radius: 4px;
          background: #f1f3f5;
          color: #1b9c45;
          padding: 8px 14px;
          font-size: 14px;
          line-height: 20px;
          cursor: pointer;
        }
        .live-sort-active {
          background: #1b9c45;
          color: #fff;
          font-weight: 600;
        }
        :global(.live-dot),
        :global(.live-text) {
          animation: liveSynchronizedPulse 1.2s ease-in-out infinite;
          font-size: 21.6px;
        }
        :global(.live-dot) {
          background-color: #1b9c45;
        }
        @keyframes liveSynchronizedPulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.3;
          }
        }
        @media (max-width: 640px) {
          .live-tab-button {
            font-size: 20px;
            line-height: 30px;
            padding: 8px 16px;
          }
        }
      `}</style>
    </div>
  )
}
